import { createRef } from "react";

import {
  getBulletin,
  getBulletinTypesPublic,
  getCrimeTypes,
  getPublicGroupBulletins,
  PaginatedResponseBulletinResource,
  PaginatedResponsePublicBulletinResource,
} from "@rubi/api-generated";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { dateToISOString } from "@/common/utils";
import { IAssociatedCrime, IPoliceService } from "@/types";

export function useAlertCrimeTypesQuery() {
  const lang =
    document.cookie
      .split("; ")
      .find((r) => r.startsWith("language="))
      ?.split("=")[1] ?? "en";
  return useQuery(
    ["alert-crime-types", lang],
    async (): Promise<IAssociatedCrime[]> => {
      const res = await getCrimeTypes({ headers: { "Accept-Language": lang } });
      return (res.data ?? []).flatMap((group) =>
        group.crimes.map((crime) => ({
          crime: crime.key,
          group: group.group_key,
          name: crime.name,
        })),
      );
    },
    { staleTime: 60 * 60 * 1000 },
  );
}

export function useCrimeLabel() {
  const { data: crimeTypes = [] } = useAlertCrimeTypesQuery();
  return (key: string): string =>
    crimeTypes.find((c) => c.crime === key)?.name ?? key;
}

export function useBulletinTypesQuery() {
  const lang =
    document.cookie
      .split("; ")
      .find((r) => r.startsWith("language="))
      ?.split("=")[1] ?? "en";
  return useQuery(
    ["bulletin-types-public", lang],
    async () => {
      const res = await getBulletinTypesPublic({
        headers: { "Accept-Language": lang },
      });
      return res.data ?? [];
    },
    { staleTime: 60 * 60 * 1000 },
  );
}

export function useBulletinTypeLabel() {
  const { data: bulletinTypes = [] } = useBulletinTypesQuery();
  return (key: string): string =>
    bulletinTypes.find((t) => t.reference_name === key)?.name ?? key;
}

export function useAlertsInfiniteQuery(
  itemsPerPage: number | null,
  associatedCrime: string[] | null,
  bulletinType: string[] | null,
  policeService: IPoliceService | null,
  incidentStartDate: string | null,
  incidentEndDate: string | null,
  issuedStartDate: string | null,
  issuedEndDate: string | null,
  searchText: string | null,
  is_draft: boolean | null,
  creator_user_id: string | null,
  similarTo: number | null,
  bounds: string | null,
  creator_org_id: string | null | undefined,
  my_agency_bulletins: boolean | null,
  is_flag = false,
  sort_by_incident_time: boolean | null,
  group_ids: number[] | null = null,
  sort_by: string | null = null,
  sort_order: string | null = null,
  resolution_status: string | null = null,
  engagement: string | null = null,
) {
  const bulletinRefs: Record<string, React.RefObject<HTMLDivElement>> = {};

  return useInfiniteQuery(
    [
      "alerts",
      associatedCrime,
      bulletinType,
      policeService,
      sort_by_incident_time,
      creator_org_id,
      incidentStartDate,
      incidentEndDate,
      issuedStartDate,
      issuedEndDate,
      searchText,
      is_draft,
      creator_user_id,
      my_agency_bulletins,
      similarTo,
      bounds,
      is_flag,
      group_ids,
      sort_by,
      sort_order,
      resolution_status,
      engagement,
    ],
    async ({ pageParam = 0 }): Promise<PaginatedResponseBulletinResource> => {
      // if amount(itemsPerPage) is not set and bounds are not set, then don't fetch
      if (!itemsPerPage && !bounds) {
        return {
          items: [],
          start_position: pageParam,
          total_count: 0,
        };
      }

      const lang =
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("language="))
          ?.split("=")[1] ?? "en";

      const res = await getBulletin({
        query: {
          start: pageParam,
          amount: itemsPerPage && itemsPerPage > 0 ? itemsPerPage : undefined,
          associated_crime: associatedCrime ?? undefined,
          type: bulletinType ?? undefined,
          issuing_police_service: policeService?.name ?? null,
          sort_by_incident_time: sort_by_incident_time ?? false,
          creator_org_id: creator_org_id ?? null,
          incident_time_start: incidentStartDate
            ? dateToISOString(incidentStartDate)
            : null,
          incident_time_end: incidentEndDate
            ? dateToISOString(incidentEndDate)
            : null,
          issued_time_start: issuedStartDate
            ? dateToISOString(issuedStartDate)
            : null,
          issued_time_end: issuedEndDate
            ? dateToISOString(issuedEndDate)
            : null,
          search_query:
            searchText && searchText !== "" ? searchText : undefined,
          is_draft: is_draft ?? false,
          creator_user_id: creator_user_id ?? null,
          my_agency_bulletins: my_agency_bulletins || null,
          similar_to: similarTo ?? null,
          bounds: bounds ?? null,
          is_flag,
          group_id: group_ids ?? undefined,
          sort_by: sort_by ?? undefined,
          sort_order: sort_order ?? undefined,
          resolution_status: resolution_status ?? undefined,
          engagement: engagement ?? undefined,
        },
        headers: { "Accept-Language": lang },
      });
      return res.data!;
    },
    {
      getNextPageParam: (lastPage, pages) => {
        if (
          (lastPage?.items?.length ?? 0) === 0 ||
          (itemsPerPage && (lastPage?.items?.length ?? 0) < itemsPerPage)
        ) {
          return undefined;
        }
        if (itemsPerPage) {
          return pages.length * itemsPerPage;
        }
        return undefined;
      },
      onSuccess: (data) => {
        data.pages.forEach((page) =>
          page.items.forEach((bulletin) => {
            bulletinRefs[bulletin.id] = createRef<HTMLDivElement>();
          }),
        );
      },
      // TODO: Figure out single record addition / mutation and restore cache
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
    },
  );
}

export function usePublicAlertsInfiniteQuery(
  itemsPerPage: number | null,
  group_id: string,
  bounds: string | null,
  is_flag: boolean,
) {
  const bulletinRefs: Record<string, React.RefObject<HTMLDivElement>> = {};
  return useInfiniteQuery(
    ["public-alerts", group_id, bounds, is_flag],
    async ({
      pageParam = 0,
    }): Promise<PaginatedResponsePublicBulletinResource> => {
      const res = await getPublicGroupBulletins({
        path: { group_id: Number(group_id) },
        query: {
          start: pageParam,
          amount: itemsPerPage && itemsPerPage > 0 ? itemsPerPage : undefined,
          bounds,
          is_flag,
        },
      });
      return res.data!;
    },
    {
      getNextPageParam: (lastPage) => {
        return (lastPage?.start_position ?? 0) + (lastPage?.items?.length ?? 0);
      },
      onSuccess: (data) => {
        data.pages.forEach((page) =>
          page.items.forEach((bulletin) => {
            bulletinRefs[bulletin.encrypted_id] = createRef<HTMLDivElement>();
          }),
        );
      },
    },
  );
}
